import React, { useContext } from "react";
import cn from "classnames";
import {
  ColumnDef,
  Table,
  TableState,
  createColumnHelper,
  getCoreRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  useReactTable,
  RowSelectionState,
  OnChangeFn,
} from "@tanstack/react-table";
import {
  CellCheckbox,
  HeaderCheckbox,
} from "oneclick-component/src/components/TableWithSelect";
import { t } from "i18next";
import { useBreakPoints } from "oneclick-component/src/providers";
import {
  BriefStation,
  IncidentShiftAccessControl,
  ShiftRequestBase,
  ShiftType,
} from "oneclick-component/src/store/apis/enhancedApi";
import { StationBadge } from "oneclick-component/src/components/Badge";
import { SELECT_COLUMN_ID } from "oneclick-component/src/components/TableWithSelect/constants";
import { HiredShiftRequestBase } from "../../../models/shiftRequest";
import { FeatureConfigContext } from "../../../providers/FeatureConfigContext";
import { ShiftRateViewModel } from "oneclick-component/src/components/ShiftRateBadge";
import ShiftBadge from "oneclick-component/src/components/ShiftRateBadge/ShiftBadge";
import ShiftT3Badge from "oneclick-component/src/components/Badge/ShiftT3Badge";
import {
  ActiveShiftListTableAction,
  ActiveShiftListTableHireStatus,
} from "./ActiveShiftListTableCells";

export interface ActiveShiftListRecord {
  info: {
    title: string;
    rate: ShiftRateViewModel | null;
    subtitle: React.ReactElement;
    isAppliableToAllAtT3: boolean;
  };
  timeRange: string;
  partTimeType: string;
  appliedRequests: ShiftRequestBase[];
  hireStatus: {
    workingStationShortCode: string;
    fulfillmentCount: number;
    hiredRequests: HiredShiftRequestBase[];
  };
  // grouping/sorting, invisible columns
  title: string;
  date: string;
  numberOfApplicants: number;
  fulfillmentCount: number;
  id: number;

  // helper info
  fullTimeCount: number;
  hiredFullTimeCount: number;
  shiftType: ShiftType;
  incidentAccessControl?: IncidentShiftAccessControl | null;
  remoteStation?: BriefStation | null;
}

const columnHelper = createColumnHelper<ActiveShiftListRecord>();

export const useActiveShiftListTableColumns = (
  hasIncident: boolean
  // eslint-disable-next-line sonarjs/cognitive-complexity
): ColumnDef<ActiveShiftListRecord, any>[] => {
  const { shouldShowT3Feature } = useContext(FeatureConfigContext);
  const { useIsSm } = useBreakPoints();
  const isSm = useIsSm();
  return [
    {
      id: SELECT_COLUMN_ID,
      size: 16,
      header: HeaderCheckbox,
      cell: CellCheckbox,
    },
    columnHelper.accessor("info", {
      header: () => t("shiftList.table.header.shiftInfo"),
      cell: ({ cell, row }) => {
        const isIncident = row.original.shiftType === "INCIDENT";
        const info = cell.getValue();
        return (
          <div className={cn("flex", "flex-row", "items-center")}>
            <div className={cn("flex", "flex-col", "flex-1")}>
              <div className={cn("flex", "gap-x-3", "items-center")}>
                <ShiftBadge rate={info.rate} isIncident={isIncident} />
                <p
                  className={cn("font-medium", "text-sm", "text-black/90", {
                    "text-red-600": isIncident,
                  })}
                >
                  {info.title}
                </p>
                {shouldShowT3Feature && !isIncident ? (
                  <>
                    <div
                      className={cn(
                        "border",
                        "border-solid",
                        "border-black/24",
                        "self-stretch",
                        "w-px",
                        "my-0.5"
                      )}
                    ></div>
                    <ShiftT3Badge active={info.isAppliableToAllAtT3} />
                  </>
                ) : null}
              </div>
              <p
                className={cn(
                  "font-normal",
                  "text-xs",
                  "leading-5",
                  "text-black/60",
                  { "text-red-600": isIncident },
                  "mt-1"
                )}
              >
                {info.subtitle}
              </p>
            </div>
            {row.original.remoteStation ? (
              <div>
                <StationBadge
                  station={row.original.remoteStation}
                  stationTeam={null}
                />
              </div>
            ) : null}
          </div>
        );
      },
    }),
    columnHelper.accessor("timeRange", {
      header: () => t("shiftList.table.header.timeRange"),
      size: 140,
      cell: ({ cell, row }) => {
        const isIncident = row.original.shiftType === "INCIDENT";
        return (
          <p
            className={cn(
              "font-normal",
              "text-sm",
              "leading-5",
              "text-gray-500",
              { "text-red-600": isIncident }
            )}
          >
            {cell.getValue()}
          </p>
        );
      },
    }),
    columnHelper.accessor("partTimeType", {
      header: () => t("shiftList.table.header.partTimeType"),
      size: 120,
      cell: ({ cell, row }) => {
        const isIncident = row.original.shiftType === "INCIDENT";
        return (
          <p
            className={cn(
              "font-normal",
              "text-sm",
              "leading-5",
              "text-gray-500",
              { "text-red-600": isIncident }
            )}
          >
            {cell.getValue()}
          </p>
        );
      },
    }),
    columnHelper.accessor("appliedRequests", {
      header: () => (
        <span className={"min-w-10"}>
          {t("shiftList.table.header.numberOfApplicant")}
        </span>
      ),
      size: isSm ? 120 : 76,
      cell: ({ cell, row }) => {
        const isIncident = row.original.shiftType === "INCIDENT";
        const requests = cell.getValue();
        const unread = requests.some(
          (req: ShiftRequestBase) =>
            (req.status === "applied" && req.borrowingRequestStatus == null) ||
            (req.status === "applied" &&
              req.borrowingRequestStatus === "approved")
        );
        return (
          <div className={cn("flex", "flex-row")}>
            <p
              className={cn(
                "font-normal",
                "text-sm",
                "leading-5",
                "text-gray-500",
                { "text-red-600": isIncident }
              )}
            >
              {requests.length}
            </p>
            {unread ? (
              <div
                className={cn(
                  "w-[6px]",
                  "h-[6px]",
                  "ml-1",
                  "bg-red-600",
                  "rounded-full"
                )}
              />
            ) : null}
          </div>
        );
      },
    }),
    columnHelper.accessor("hireStatus", {
      header: () => (
        <span className={"whitespace-normal"}>
          {t("shiftList.table.header.ratioOfHire")}
        </span>
      ),
      size: 160,
      cell: ActiveShiftListTableHireStatus,
    }),
    columnHelper.display({
      id: "action",
      header: () => (
        <span className="w-full">{t("shiftList.table.header.action")}</span>
      ),
      size: hasIncident ? 220 : 90,
      cell: ActiveShiftListTableAction,
    }),
    columnHelper.accessor("title", {
      enableHiding: true,
    }),
    columnHelper.accessor("date", {
      enableHiding: true,
    }),
    columnHelper.accessor("fulfillmentCount", {
      enableHiding: true,
    }),
    columnHelper.accessor("numberOfApplicants", {
      enableHiding: true,
    }),
    columnHelper.accessor("id", {
      enableHiding: true,
    }),
    columnHelper.accessor("shiftType", {
      enableHiding: true,
    }),
  ];
};

export const useActiveShiftListTable = (
  data: ActiveShiftListRecord[],
  onRowSelectionChange: OnChangeFn<RowSelectionState>,
  state?: Partial<TableState>
): Table<ActiveShiftListRecord> => {
  const hasIncident = data.filter((s) => s.shiftType === "INCIDENT").length > 0;
  const shiftListTableColumns = useActiveShiftListTableColumns(hasIncident);
  const table = useReactTable({
    data,
    columns: shiftListTableColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    defaultColumn: {
      minSize: 0,
      size: Number.MAX_SAFE_INTEGER,
      maxSize: Number.MAX_SAFE_INTEGER,
    },
    manualSorting: true,
    groupedColumnMode: false,
    enableRowSelection: true,
    onRowSelectionChange,
    state,
  });
  return table;
};
